import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useParams, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/auth/AuthContext";
import { Protected, DashboardRedirect } from "@/auth/Protected";
import { api } from "@/lib/api";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import SuperAdmin from "@/pages/SuperAdmin";
import AdminApp from "@/pages/AdminApp";
import { AdminLangProvider } from "@/auth/AdminLangCtx";
import BankPublic from "@/pages/BankPublic";
import ErrorBoundary from "@/components/ErrorBoundary";

const DEMO_DOMAIN = "icz.finance";
const BANKS_DOMAIN = "edbks.com";
const ALLOWED_BANK_DOMAINS = ["edbks.com", "ixt.finance", "ixt.app"];
const PLATFORM_HOSTS = ["neobk.pro", "www.neobk.pro"];

// Decide what the SPA must render based on the current hostname.
//   neobk.pro / www.neobk.pro / localhost / *.preview.emergentagent.com
//     -> "platform"    (landing, /login, /register, /admin, /super-admin)
//   icz.finance / www.icz.finance
//     -> "bank"        (BankPublic for slug "_demo" — demo bank)
//   {slug}.edbks.com  (primary bank domain, current)
//   {slug}.ixt.finance / {slug}.ixt.app  (legacy — nginx now 301s them)
//     -> "bank"        (BankPublic for that slug)
//   edbks.com / ixt.finance / ixt.app / www.* (apex or multi-level)
//     -> "not_found"   (neutral 404 page — NEVER fall back to platform)
function resolveApp() {
  const host = (window.location.hostname || "").toLowerCase();
  if (host === DEMO_DOMAIN || host === `www.${DEMO_DOMAIN}`) {
    return { mode: "bank", slug: "_demo" };
  }
  for (const domain of ALLOWED_BANK_DOMAINS) {
    if (host === domain || host === `www.${domain}`) {
      return { mode: "not_found" };
    }
    const suffix = `.${domain}`;
    if (host.endsWith(suffix)) {
      const sub = host.slice(0, -suffix.length);
      if (sub && !sub.includes(".") && sub !== "www") {
        return { mode: "bank", slug: sub };
      }
      return { mode: "not_found" };
    }
  }
  return { mode: "platform" };
}

function AnnouncementBanner() {
  const [banner, setBanner] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const host = (window.location.hostname || "").toLowerCase();
    if (!PLATFORM_HOSTS.includes(host)) return;
    api.get("/public/announcement")
      .then((r) => setBanner(r.data))
      .catch(() => {});
  }, []);

  if (!banner?.enabled || dismissed) return null;

  return (
    <div style={{
      background: "linear-gradient(135deg, #3B82F6 0%, #8B5CF6 100%)",
      color: "#fff",
      padding: "0.625rem 1rem",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.75rem",
      fontSize: "0.875rem",
      fontWeight: 500,
      lineHeight: 1.4,
      position: "sticky",
      top: 0,
      zIndex: 9999,
    }}>
      <span style={{ textAlign: "center", maxWidth: "48rem" }}>{banner.text}</span>
      <button
        onClick={() => setDismissed(true)}
        aria-label="Fermer"
        style={{
          flexShrink: 0,
          background: "rgba(255,255,255,.25)",
          border: "none",
          borderRadius: "50%",
          width: "1.375rem",
          height: "1.375rem",
          cursor: "pointer",
          color: "#fff",
          fontSize: "1rem",
          lineHeight: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >×</button>
    </div>
  );
}

const appResolution = resolveApp();

// ── Pre-React metadata scrub ─────────────────────────────────────────────────
// As soon as App.js is imported (i.e. before React's first render), strip every
// platform-branded metadata tag (Neobk.Pro title, description, OG, Twitter…) on
// bank domains. This prevents the tab from flashing "Neobk.Pro" while React boots
// and ensures any crawler that DOES execute JS sees neutral meta until the bank
// payload arrives. Server-side crawler routing (Nginx → /api/og/bank/{slug}) is
// handled separately on the prod stack — this block is the client-side safety net.
(function scrubPlatformMeta() {
  if (appResolution.mode === "platform") return; // keep Neobk.Pro branding for platform hosts
  try {
    // 1. Title — neutral placeholder until BankPublic resolves the bank name
    document.title = appResolution.mode === "bank" ? appResolution.slug : "";

    // 2. Remove any platform description / OG / twitter meta in the static HTML
    const KILL_SELECTORS = [
      'meta[name="description"]',
      'meta[property^="og:"]',
      'meta[name^="twitter:"]',
      'link[rel="canonical"]',
    ];
    KILL_SELECTORS.forEach((sel) => {
      document.querySelectorAll(sel).forEach((el) => el.parentNode && el.parentNode.removeChild(el));
    });

    // 3. Replace favicon with a neutral 1×1 transparent PNG until React swaps it
    //    for the bank's logo/initial. Avoids flashing the platform favicon.
    const NEUTRAL_ICO = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNgAAIAAAUAAen63NgAAAAASUVORK5CYII=";
    const link = document.querySelector("link[rel='icon']") || document.createElement("link");
    link.rel = "icon";
    link.href = NEUTRAL_ICO;
    link.type = "image/png";
    if (!link.parentNode) document.head.appendChild(link);
  } catch (_) { /* head not ready — ignore */ }
})();

function BankDomainNotFound() {
  useEffect(() => {
    document.title = "404";
  }, []);
  return (
    <div style={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: "#0a0a0a",
      color: "#e5e7eb",
      fontFamily: "system-ui, -apple-system, sans-serif",
      padding: "2rem",
      textAlign: "center",
    }}>
      <h1 style={{ fontSize: "2rem", fontWeight: 600, letterSpacing: "0.02em", color: "#e5e7eb", margin: 0 }}>404 error</h1>
    </div>
  );
}

// Redirect neobk.pro/bank/:slug/* → slug.ixt.finance/*
// In preview / localhost environments where *.ixt.finance DNS is not available,
// render BankPublic inline so admins (and QA) can preview the bank UI.
function BankRedirect() {
  const { slug } = useParams();
  const location = useLocation();
  const host = (typeof window !== "undefined" ? window.location.hostname : "").toLowerCase();
  const isPreviewEnv = host.includes("preview.emergentagent.com") || host === "localhost" || host === "127.0.0.1";

  useEffect(() => {
    if (isPreviewEnv) return; // don't redirect in preview — render inline below
    const rest = location.pathname.replace(`/bank/${slug}`, "") || "/";
    window.location.replace(`https://${slug}.${BANKS_DOMAIN}${rest}${location.search}`);
  }, []);

  if (isPreviewEnv) {
    return <BankPublic slugOverride={slug} />;
  }
  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280", fontSize: "0.9rem" }}>
      Redirection…
    </div>
  );
}

function App() {
  // ixt.finance apex / www.ixt.finance / multi-level *.ixt.finance
  if (appResolution.mode === "not_found") {
    return (
      <div className="App">
        <BankDomainNotFound />
      </div>
    );
  }

  // {slug}.ixt.finance — bank customer SPA only
  if (appResolution.mode === "bank") {
    return (
      <div className="App">
        <AuthProvider>
          <ErrorBoundary>
            <BrowserRouter>
              <Routes>
                <Route path="/*" element={<BankPublic slugOverride={appResolution.slug} />} />
              </Routes>
            </BrowserRouter>
          </ErrorBoundary>
          <Toaster position="top-right" richColors closeButton />
        </AuthProvider>
      </div>
    );
  }

  // Platform (neobk.pro, localhost, Emergent preview, custom admin domain)
  return (
    <div className="App">
      <AuthProvider>
        <ErrorBoundary>
        <AnnouncementBanner />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/dashboard" element={<DashboardRedirect />} />
            <Route
              path="/super-admin/*"
              element={
                <Protected roles={["super_admin"]}>
                  <SuperAdmin />
                </Protected>
              }
            />
            <Route
              path="/admin/*"
              element={
                <Protected roles={["admin"]}>
                  <AdminLangProvider>
                    <AdminApp />
                  </AdminLangProvider>
                </Protected>
              }
            />
            <Route path="/bank/:slug/*" element={<BankRedirect />} />
          </Routes>
        </BrowserRouter>
        </ErrorBoundary>
        <Toaster position="top-right" richColors closeButton />
      </AuthProvider>
    </div>
  );
}

export default App;
